import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  Loader2,
  MapPinned,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { handleApiError } from '@/api/client'
import {
  gameApi,
  type GameLessonMapping,
  type GameLessonMappingSaveRequest,
} from '@/api/games'
import {
  GameLessonPathPicker,
  gameLessonPathKey,
  type GameLessonPathSelection,
} from './GameLessonPathPicker'

interface GameLessonsPanelProps {
  gameKey: string | null
  onClose: () => void
}

export interface GameLessonMappingDraft extends GameLessonPathSelection {
  sortOrder: number
}

export function createActiveGameLessonDrafts(
  mappings: GameLessonMapping[]
): GameLessonMappingDraft[] {
  return mappings
    .filter((mapping) => mapping.status === 'ACTIVE')
    .map(({ curriculum, course, module, lesson, sortOrder }) => ({
      curriculum,
      course,
      module,
      lesson,
      sortOrder,
    }))
}

export function buildGameLessonMappingSaveRequest(
  drafts: GameLessonMappingDraft[]
): GameLessonMappingSaveRequest {
  return {
    mappings: drafts.map((draft) => ({
      curriculumContentId: draft.curriculum.contentId,
      courseContentId: draft.course.contentId,
      moduleContentId: draft.module.contentId,
      lessonContentId: draft.lesson.contentId,
      sortOrder: draft.sortOrder,
    })),
  }
}

export function GameLessonsPanel({ gameKey, onClose }: GameLessonsPanelProps) {
  const {
    data,
    dataUpdatedAt,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'game-lesson-mappings', gameKey],
    queryFn: () => gameKey ? gameApi.getLessonMappings(gameKey) : Promise.resolve([]),
    enabled: Boolean(gameKey),
  })

  if (!gameKey) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex justify-end bg-slate-950/45 backdrop-blur-[1px]"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-2xl flex-col bg-slate-50 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        aria-label={`${gameKey} 레슨 경로 연결 관리`}
      >
        <header className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">
              <MapPinned className="size-4" />
              Lesson route map
            </div>
            <h2 className="text-lg font-bold text-slate-900">레슨 경로 연결 관리</h2>
            <p className="mt-1 text-xs text-slate-500">
              게임 키 <span className="font-mono text-slate-700">{gameKey}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="닫기"
          >
            <X className="size-5" />
          </button>
        </header>

        {isLoading ? (
          <GameLessonsPanelFeedback state="loading" />
        ) : error ? (
          <GameLessonsPanelFeedback
            state="error"
            onRetry={() => refetch()}
          />
        ) : (
          <GameLessonMappingEditor
            key={`${gameKey}:${dataUpdatedAt}`}
            gameKey={gameKey}
            mappings={data ?? []}
            onClose={onClose}
          />
        )}
      </aside>
    </div>,
    document.body
  )
}

interface GameLessonsPanelFeedbackProps {
  state: 'loading' | 'error'
  onRetry?: () => void
}

export function GameLessonsPanelFeedback({
  state,
  onRetry,
}: GameLessonsPanelFeedbackProps) {
  if (state === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 className="size-5 animate-spin text-blue-500" />
        연결 정보를 불러오는 중입니다.
      </div>
    )
  }

  return (
    <div className="m-6 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
      <p className="font-medium">연결 정보를 불러오지 못했습니다.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-1.5 font-medium hover:text-red-900"
      >
        <RefreshCw className="size-4" />
        다시 시도
      </button>
    </div>
  )
}

interface GameLessonMappingEditorProps {
  gameKey: string
  mappings: GameLessonMapping[]
  onClose: () => void
}

function GameLessonMappingEditor({
  gameKey,
  mappings,
  onClose,
}: GameLessonMappingEditorProps) {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<GameLessonMappingDraft[]>(
    () => createActiveGameLessonDrafts(mappings)
  )
  const staleMappings = useMemo(
    () => mappings.filter((mapping) => mapping.status === 'STALE'),
    [mappings]
  )
  const existingPathKeys = useMemo(
    () => new Set(drafts.map(gameLessonPathKey)),
    [drafts]
  )

  const saveMutation = useMutation({
    mutationFn: (request: GameLessonMappingSaveRequest) =>
      gameApi.replaceLessonMappings(gameKey, request),
    onSuccess: async () => {
      toast.success('게임의 레슨 경로 연결을 저장했습니다.')
      onClose()
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'games'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin', 'game-lesson-mappings', gameKey],
        }),
      ])
    },
    onError: (saveError) => {
      toast.error(handleApiError(saveError))
    },
  })

  const handlePick = (selection: GameLessonPathSelection) => {
    const pathKey = gameLessonPathKey(selection)
    setDrafts((current) => {
      if (current.some((draft) => gameLessonPathKey(draft) === pathKey)) {
        return current
      }
      const nextSortOrder = current.reduce(
        (maximum, draft) => Math.max(maximum, draft.sortOrder),
        -1
      ) + 1
      return [...current, { ...selection, sortOrder: nextSortOrder }]
    })
  }

  const handleSortOrder = (pathKey: string, sortOrder: number) => {
    setDrafts((current) => current.map((draft) =>
      gameLessonPathKey(draft) === pathKey
        ? { ...draft, sortOrder: Math.max(0, sortOrder) }
        : draft
    ))
  }

  const handleRemove = (pathKey: string) => {
    setDrafts((current) =>
      current.filter((draft) => gameLessonPathKey(draft) !== pathKey)
    )
  }

  return (
    <>
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">저장할 활성 경로</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                같은 레슨이라도 계층 경로가 다르면 별도 연결로 관리됩니다.
              </p>
            </div>
            <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
              {drafts.length}개
            </span>
          </div>

          {drafts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center">
              <MapPinned className="mx-auto mb-2 size-6 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">연결된 레슨 경로가 없습니다.</p>
              <p className="mt-1 text-xs text-slate-400">아래 트리에서 레슨을 추가하세요.</p>
            </div>
          ) : (
            <ol className="space-y-2">
              {drafts.map((draft) => {
                const pathKey = gameLessonPathKey(draft)
                return (
                  <li
                    key={pathKey}
                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <PathBreadcrumb path={draft} />
                    <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                      <label className="flex items-center gap-2 text-xs text-slate-500">
                        정렬 순서
                        <input
                          type="number"
                          min={0}
                          value={draft.sortOrder}
                          onChange={(event) =>
                            handleSortOrder(pathKey, Number(event.target.value))
                          }
                          className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-right text-sm text-slate-800"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleRemove(pathKey)}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="size-3.5" />
                        제거
                      </button>
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
        </section>

        {staleMappings.length > 0 && (
          <StaleGameLessonMappings mappings={staleMappings} />
        )}

        <section>
          <div className="mb-3">
            <h3 className="text-sm font-bold text-slate-900">레슨 경로 추가</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              커리큘럼부터 펼쳐 정확한 코스·모듈·레슨 문맥을 선택하세요.
            </p>
          </div>
          <GameLessonPathPicker
            existingPathKeys={existingPathKeys}
            onPick={handlePick}
          />
        </section>
      </div>

      <footer className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
        <p className="text-xs text-slate-500">
          저장하면 이 게임의 전체 경로 연결이 교체됩니다.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saveMutation.isPending}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() =>
              saveMutation.mutate(buildGameLessonMappingSaveRequest(drafts))
            }
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            저장
          </button>
        </div>
      </footer>
    </>
  )
}

export function StaleGameLessonMappings({
  mappings,
}: {
  mappings: GameLessonMapping[]
}) {
  return (
    <section className="rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div className="flex gap-3">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-amber-900">
            유효하지 않은 기존 경로 {mappings.length}개
          </h3>
          <p className="mt-1 text-xs leading-5 text-amber-800">
            콘텐츠 비활성화 또는 계층 변경으로 사용할 수 없습니다. 저장하면 아래 연결은 제거됩니다.
          </p>
          <ul className="mt-3 space-y-2">
            {mappings.map((mapping, index) => (
              <li
                key={mapping.mappingId ?? `${gameLessonPathKey(mapping)}:${index}`}
                className="rounded-lg border border-amber-200 bg-white/70 p-3"
              >
                <PathBreadcrumb path={mapping} />
                {mapping.staleReason && (
                  <p className="mt-2 text-xs font-medium text-amber-700">
                    {mapping.staleReason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function PathBreadcrumb({ path }: { path: GameLessonPathSelection }) {
  const nodes = [path.curriculum, path.course, path.module, path.lesson]
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
      {nodes.map((node, index) => (
        <span key={`${node.contentId}:${index}`} className="contents">
          {index > 0 && <ArrowRight className="size-3 shrink-0 text-slate-300" />}
          <span
            className={index === nodes.length - 1
              ? 'font-semibold text-slate-900'
              : 'text-slate-500'}
          >
            {node.title ?? `콘텐츠 ${node.contentId}`}
          </span>
        </span>
      ))}
    </div>
  )
}
